import { computed, inject, ref, shallowRef } from 'vue'
import handleBudgets from '@/services/modules/procurement/budget'
import { downloadBlob } from '@/services/utils/global.js'

export default function useBudgetHeadSheet(getBudgetId, getQuery, getBudgetName, getTab, onApplied) {
    const showError = inject('showError')
    const showSuccess = inject('showSuccess')
    const { exportBudgetHeadSheet, previewBudgetHeadSheet, resolveBudgetHeadSheetRows, importBudgetHeadSheet } = handleBudgets()

    const step = ref('upload')
    const file = ref(null)
    const review = shallowRef(null)
    const result = ref(null)
    const isExporting = ref(false)
    const isReading = ref(false)
    const isApplying = ref(false)
    const overrides = ref({})
    const originals = ref({})
    const busyHeads = ref([])
    let readId = 0
    let editQueue = Promise.resolve()

    const columnOf = { quantity: 'Qty', rate: 'Rate', amount: 'Amount' }
    const splitLabelOf = { period_qty: 'Qty', period_amount: 'Amount', team_qty: 'Qty' }

    const tab = computed(() => getTab())

    const budgetName = computed(() => getBudgetName())

    const changeCount = computed(() => {
        if (! review.value) return 0

        return review.value.summary.new + review.value.summary.updated
    })

    const errorCount = computed(() => {
        if (! review.value) return 0

        return review.value.summary.error
    })

    const editedHeads = computed(() => Object.keys(overrides.value).map(Number))

    const canApply = computed(() => changeCount.value > 0 && errorCount.value === 0 && ! isApplying.value && busyHeads.value.length < 1)

    const reset = () => {
        readId++
        step.value = 'upload'
        file.value = null
        review.value = null
        result.value = null
        isReading.value = false
        overrides.value = {}
        originals.value = {}
        busyHeads.value = []
    }

    const saveBlob = (blob) => {
        downloadBlob(blob, `Budget Heads - ${getBudgetName()} - ${tab.value.name}.xlsx`)
    }

    const saveOrExplain = async (blob) => {
        if (! blob.type.includes('json')) {
            saveBlob(blob)
            return
        }

        const response = JSON.parse(await blob.text())

        showError(response.message)
    }

    const exportSheet = async () => {
        if (isExporting.value) return

        isExporting.value = true

        try {
            const response = await exportBudgetHeadSheet(getBudgetId(), getQuery())

            await saveOrExplain(response.data)
        } catch (error) {
            showError('Something went wrong. Please try again.')
        } finally {
            isExporting.value = false
        }
    }

    const buildForm = (signature) => {
        const form = new FormData()

        form.append('sheet', file.value)
        form.append('signature', signature)
        form.append('overrides', JSON.stringify(Object.values(overrides.value)))

        return form
    }

    const summarize = (rows, ignored) => {
        const summary = { total: rows.length, new: 0, updated: 0, unchanged: 0, error: 0, ignored, current_amount: 0, new_amount: 0, current_quantity: 0, new_quantity: 0 }

        rows.forEach(row => {
            summary[row.status]++

            if (row.status === 'error') return

            summary.current_amount += row.old_amount
            summary.new_amount += row.amount
            summary.current_quantity += row.old_quantity
            summary.new_quantity += row.quantity
        })

        return summary
    }

    const replaceRows = (resolved) => {
        const byHead = {}

        resolved.forEach(row => {
            byHead[row.project_account_id] = row
        })

        const rows = review.value.rows.map(row => byHead[row.project_account_id] || row)

        review.value = { ...review.value, rows, summary: summarize(rows, review.value.summary.ignored) }
    }

    const typedOrShown = (row, field) => {
        if (row.errors.some(error => error.column === columnOf[field])) return row.cells[field]

        return row[field]
    }

    const cellsOf = (row) => ({
        project_account_id: row.project_account_id,
        row_no: row.row_no,
        quantity: typedOrShown(row, 'quantity'),
        rate: typedOrShown(row, 'rate'),
        amount: null,
        period_qty: review.value.periods.map(() => null),
        period_amount: review.value.periods.map(() => null),
        team_qty: review.value.members.map(() => null),
    })

    const isSplitField = (field) => Object.keys(splitLabelOf).includes(field)

    const splitNamesOf = (field) => {
        if (field === 'team_qty') return review.value.members

        return review.value.periods
    }

    const splitInputOf = (row, field) => row.cells[field] || splitNamesOf(field).map(() => null)

    const shownSplitOf = (row, field) => {
        const typed = splitInputOf(row, field)
        const names = splitNamesOf(field)

        return names.map((name, index) => {
            if (row.errors.some(error => error.column === `${name} ${splitLabelOf[field]}`)) return typed[index]

            return row[field][index]
        })
    }

    const splitCellsOf = (row, field, index, value) => {
        const split = shownSplitOf(row, field)

        split[index] = value || 0

        return {
            project_account_id: row.project_account_id,
            row_no: row.row_no,
            quantity: row.cells.quantity,
            rate: row.cells.rate,
            amount: row.cells.amount,
            period_qty: splitInputOf(row, 'period_qty'),
            period_amount: splitInputOf(row, 'period_amount'),
            team_qty: splitInputOf(row, 'team_qty'),
            [field]: split,
        }
    }

    const editedCellsOf = (row, field, index, value) => {
        if (isSplitField(field)) return splitCellsOf(row, field, index, value)

        return { ...cellsOf(row), [field]: value }
    }

    const isEditableField = (row, field) => {
        if (isSplitField(field)) return field !== 'period_amount' || ! row.amount_locked
        if (! review.value.layout.typed.includes(field)) return false

        return field === 'quantity' || ! row.amount_locked
    }

    const keepEdits = (edits, resolved) => {
        const kept = { ...overrides.value }
        const files = { ...originals.value }

        resolved.forEach(row => {
            const current = review.value.rows.find(item => item.project_account_id === row.project_account_id)

            files[row.project_account_id] = files[row.project_account_id] || current
            kept[row.project_account_id] = { ...edits[row.project_account_id], signature: row.signature }
        })
        originals.value = files
        overrides.value = kept
        replaceRows(resolved)
    }

    const resolveEdits = async (list) => {
        const edits = {}

        list.forEach(cells => {
            edits[cells.project_account_id] = cells
        })
        busyHeads.value = [...busyHeads.value, ...list.map(cells => cells.project_account_id)]

        try {
            const response = await resolveBudgetHeadSheetRows(getBudgetId(), { rows: list }, getQuery())

            if (! review.value) return

            if (! response.status) {
                showError(response.message)
                return
            }

            keepEdits(edits, response.data.rows)
        } catch (error) {
            showError('The change could not be checked. Please try again.')
        } finally {
            busyHeads.value = busyHeads.value.filter(headId => ! edits[headId])
        }
    }

    const applyEdit = async (headId, field, index, value) => {
        if (! review.value) return

        const row = review.value.rows.find(item => item.project_account_id === headId)

        if (! row || ! row.cells || ! isEditableField(row, field)) return

        await resolveEdits([editedCellsOf(row, field, index, value)])
    }

    const editCell = ({ row, field, index, value }) => {
        editQueue = editQueue
            .then(() => applyEdit(row.project_account_id, field, index, value))
            .catch(() => showError('The change could not be checked. Please try again.'))

        return editQueue
    }

    const restoreRow = (headId) => {
        const original = originals.value[headId]

        if (! original || ! review.value) return

        const kept = { ...overrides.value }
        const files = { ...originals.value }

        delete kept[headId]
        delete files[headId]
        overrides.value = kept
        originals.value = files
        replaceRows([original])
    }

    const undoRow = (row) => {
        editQueue = editQueue
            .then(() => restoreRow(row.project_account_id))
            .catch(() => showError('The change could not be undone. Please try again.'))

        return editQueue
    }

    const repeatEdits = async () => {
        if (! review.value) return

        const heads = review.value.rows.map(row => row.project_account_id)
        const list = Object.values(overrides.value)
            .filter(edit => heads.includes(edit.project_account_id))
            .map(edit => ({ ...edit, signature: undefined }))

        overrides.value = {}
        originals.value = {}

        if (list.length < 1) return

        await resolveEdits(list)
    }

    const readSheet = async (selectedFile) => {
        if (isReading.value) return

        readId++
        const currentRead = readId

        file.value = selectedFile
        isReading.value = true

        try {
            const response = await previewBudgetHeadSheet(getBudgetId(), buildForm(''), getQuery())

            if (currentRead !== readId) return

            if (! response.status) {
                showError(response.message)
                reset()
                return
            }

            review.value = response.data
            step.value = 'review'
            isReading.value = false
        } catch (error) {
            if (currentRead !== readId) return

            showError('The file could not be read. Please choose it again.')
            reset()
        }
    }

    const applySheet = async () => {
        if (! canApply.value) return

        isApplying.value = true

        try {
            const response = await importBudgetHeadSheet(getBudgetId(), buildForm(review.value.signature), getQuery())

            if (! response.status) {
                showError(response.message)
                await readSheet(file.value)
                await repeatEdits()
                return
            }

            result.value = response.data
            step.value = 'done'
            showSuccess(response.message)
            onApplied()
        } catch (error) {
            showError('The file could not be sent. If you changed it after the review, please upload it again.')
            reset()
        } finally {
            isApplying.value = false
        }
    }

    return {
        tab,
        budgetName,
        step,
        file,
        review,
        result,
        isExporting,
        isReading,
        isApplying,
        busyHeads,
        editedHeads,
        changeCount,
        errorCount,
        canApply,
        reset,
        exportSheet,
        readSheet,
        applySheet,
        editCell,
        undoRow,
        isEditableField,
    }
}
